import { q } from "@eai-toolkit/client";
import type { Filters } from "@store/utils/queryStateUtils";

import { getDateRangeForQuery } from "@helpers/utils";

type QArg = Parameters<typeof q>[number];

// Resolves a queryState (frontend) field name to the backend field name used on
// the wire. Defaults to identity; pass a resolver (e.g. the same one used for
// `order`) when they differ, such as `expires` → `expiration`.
export function filtersToQ<T extends object>(
  filters: Filters<T>,
  resolveFieldName: (fieldName: string) => string = (fieldName) => fieldName,
) {
  const args = [...filters.entries()].reduce<QArg[]>(
    (
      args,
      [
        rawFieldName,
        { valueList, booleanValueList, fuzzyMatch, range, comparisons },
      ],
    ) => {
      const fieldName = resolveFieldName(rawFieldName);
      const values = valueList.map((value) => value ?? "");
      if (values.length) {
        args.push({
          fieldName,
          operator: "in",
          value: values,
        });
      }
      // check if our boolean list contains either true or false but not both
      if (new Set(booleanValueList).size === 1) {
        args.push({
          fieldName,
          operator: "eq",
          value: String(booleanValueList[0]),
        });
      }
      if (fuzzyMatch) {
        args.push({
          fieldName,
          operator: "ilike",
          value: `%${fuzzyMatch}%`,
        });
      }
      if (range.length > 0) {
        const isClosedRange = range[0] === "isWeek" || range[0] === "isToday";
        let start = String(range[0]);
        let end = String(range[1]);
        if (isClosedRange) {
          [start, end] = getDateRangeForQuery(range[0] as "isWeek" | "isToday");
        }

        args.push({
          fieldName,
          operator: "between",
          value: [start, end],
        });
      }
      // Open-ended comparisons (lt/gt/le/ge) — e.g. "expiration before X".
      // Each is ANDed; a field may carry several (a lower and upper bound).
      for (const { operator, value } of comparisons ?? []) {
        args.push({ fieldName, operator, value });
      }
      return args;
    },
    [],
  );
  return args.length ? q(...args) : undefined;
}
